import { PermissionCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import {
  useKbArticle,
  useKbVersions,
  useRestoreKbVersion,
} from '@/products/helpdesk/features/kb/use-kb';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PersonChip } from '@/platform/components/person-chip';

export function KbHistoryPage() {
  const { slug = '' } = useParams();
  const me = useMe();
  const article = useKbArticle(slug);
  const versions = useKbVersions(slug);
  const restore = useRestoreKbVersion(slug);
  const canWrite = hasPermission(me.data, PermissionCode.KB_WRITE);

  if (article.isPending || versions.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Historie wird geladen …
      </p>
    );
  }

  if (!article.data) {
    return <p>Artikel nicht gefunden.</p>;
  }

  async function onRestore(version: number) {
    try {
      const updated = await restore.mutateAsync(version);
      toast.success(`Version ${version} wiederhergestellt.`);
      window.location.assign(`/helpdesk/wissen/${updated.slug}/historie`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Wiederherstellung fehlgeschlagen.',
      );
    }
  }

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/helpdesk/wissen/${article.data.slug}`}>Zurück zum Artikel</Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Historie: {article.data.title}</h1>
      </div>
      <ol className="space-y-4">
        {(versions.data?.items ?? []).map((item) => (
          <li key={item.id} className="rounded-md border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium">
                Version {item.version}
                {item.isCurrent ? ' (aktuell)' : ''} · {item.title}
              </p>
              {canWrite && !item.isCurrent && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void onRestore(item.version)}
                >
                  Wiederherstellen
                </Button>
              )}
            </div>
            <p className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
              <PersonChip
                displayName={item.author.displayName}
                profilePhotoUrl={item.author.profilePhotoUrl}
                avatarClassName="size-5"
              />
              <span>· {formatDateTime(item.createdAt)}</span>
              {item.hasEmbedding ? <span>· Embedding</span> : null}
            </p>
            {item.summary && <p className="mt-2 text-sm">{item.summary}</p>}
            <div
              className="ticket-prose mt-3 text-sm"
              dangerouslySetInnerHTML={{ __html: item.body }}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}
